import { Cancel, type IconToken } from '@1debit/icons';
import { type MouseEvent, useEffect } from 'react';

import { BannerImage } from './subcomponents/BannerImage';

import { Button } from '../../server/Button/Button';
import { Link } from '../../server/Link/Link';
import { type ImageToken } from '../../server/RemoteImage/RemoteImage';
import { type ImageAssetData } from '../../server/ServerDrivenImage/ServerDrivenImage';
import { getIconByKey } from '../../server/utils/assets';
import { classNames } from '../../server/utils/classNames';
import { type BannerAnalyticsData, useBannerShownAnalytics } from '../AnalyticsProvider/hooks/useBannerShownAnalytics';
import { MarkdownContent } from '../MarkdownContent';

export type SmallBannerVariant = 'primary' | 'secondary' | 'inverse' | 'green' | 'darkGreen' | 'yellow' | 'grey';
export type SmallBannerContainerType = 'fullWidth' | 'card';
export type SmallBannerImagePosition = 'centerLeft' | 'bottomRight';
export type SmallBannerCtaLayout = 'inlineLink' | 'button';

type CtaProps = {
	label: string;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
	ctaType?: SmallBannerCtaLayout;
	'aria-label'?: string;
};

type BaseProps = {
	analyticsData: BannerAnalyticsData;
	variant: SmallBannerVariant;
	container: SmallBannerContainerType;
	title?: string;
	/** Supports markdown */
	body?: string;
	className?: string;
	ctaProps?: CtaProps;
	onCloseClick?: (event: MouseEvent<HTMLButtonElement>) => void;
	/* This can be used to perform custom action when link in markdown is clicked.*/
	onMarkdownLinkClick?: (url: string) => void;
};

type SmallBannerAssetsProps =
	| {
			/** When present, imageUrl, imageAsset and imagePosition will be ignored */
			icon?: IconToken;
			imageUrl?: never;
			imagePosition?: never;
			imageAsset?: never;
	  }
	| {
			imageUrl?: string;
			imagePosition?: SmallBannerImagePosition;
			imageAsset?: ImageAssetData | ImageToken;
			icon?: never;
	  };

export type SmallBannerProps = BaseProps & SmallBannerAssetsProps;

export const SmallBanner = ({
	analyticsData,
	variant,
	container,
	title,
	body,
	ctaProps,
	onCloseClick,
	onMarkdownLinkClick,
	icon,
	imageUrl,
	imagePosition = 'centerLeft',
	imageAsset,
	className,
}: SmallBannerProps) => {
	const canRenderImage = !icon && (!!imageUrl || !!imageAsset);
	const shouldRenderImageOnLeft = canRenderImage && imagePosition === 'centerLeft';
	const shouldRenderImageOnRight = canRenderImage && imagePosition === 'bottomRight';

	const contentStyle =
		variant === 'inverse' || variant === 'darkGreen' ? 'text-content-inverse' : 'text-content-primary';
	const containerStyle = classNames(
		'w-full flex items-start gap-4 py-4 px-4 overflow-hidden',
		'has-[#small-banner-cta:active]:scale-[.98] has-[a:active]:scale-[.98] transition-all',
		{
			'rounded-xl': container === 'card',
			'pb-0 pr-0': shouldRenderImageOnRight,
			'pl-0 py-0': shouldRenderImageOnLeft,
		},
		contentStyle,
		SMALL_BANNER_BG_COLOR[variant],
		className,
	);
	const { ctaType = 'inlineLink' } = ctaProps || {};

	const ctaButtonTheme = getCTAButtonTheme(variant, ctaType);

	const Icon = icon ? getIconByKey(icon) : null;
	const { unique_id: analyticsId } = analyticsData ?? {};

	const { handleBannerShown } = useBannerShownAnalytics(analyticsData);

	// send analytics on mount
	useEffect(() => handleBannerShown(), [handleBannerShown]);

	return (
		<div className={containerStyle}>
			{Icon && (
				<div className="self-start">
					<Icon />
				</div>
			)}
			{shouldRenderImageOnLeft && (
				<div className="self-center flex items-center">
					<BannerImage alt={title} imageAsset={imageAsset} imageUrl={imageUrl} imagePosition={imagePosition} />
				</div>
			)}

			<div
				className={classNames('flex flex-1 flex-col sm:flex-row sm:justify-between sm:items-center gap-y-2 gap-x-4', {
					'pb-4 sm:flex-col sm:items-start sm:justify-start': shouldRenderImageOnRight,
					'py-4': shouldRenderImageOnLeft,
				})}>
				<div className="flex flex-col gap-1">
					{title && <h2 className="font-bold">{title}</h2>}
					{body && (
						<MarkdownContent
							analyticsId={null}
							components={{
								a: {
									component: ({ children, href }) => {
										if (!href) return <span>{children}</span>;
										if (onMarkdownLinkClick) {
											return (
												<Link
													analyticsId={`${analyticsId}_markdown_link_click`}
													className={classNames('w-fit active:scale-100', contentStyle)}
													onClick={() => onMarkdownLinkClick(href)}>
													{children}
												</Link>
											);
										}

										return (
											<Link
												analyticsId={`${analyticsId}_markdown_link_click`}
												className={classNames('w-fit active:scale-100', contentStyle)}
												href={href}>
												{children}
											</Link>
										);
									},
								},
							}}>
							{body}
						</MarkdownContent>
					)}
				</div>

				{ctaProps && (
					<Button
						id="small_banner_cta"
						analyticsId={`${analyticsId}_cta_click`}
						aria-label={ctaProps['aria-label']}
						onClick={ctaProps.onClick}
						variant={ctaButtonTheme}
						size="small"
						className={classNames('w-fit sm:min-w-max underline-offset-4', {
							'active:bg-none!important': ctaButtonTheme === 'link',
							'bg-bg-raised-primary active:bg-accent-grey-100 hover:bg-accent-grey-100': ctaButtonTheme === 'secondary', // override button styles for this exact use case in small banner
						})}>
						{ctaProps.label}
					</Button>
				)}
			</div>

			<div className={classNames('flex flex-col self-stretch', { 'py-4': shouldRenderImageOnLeft })}>
				{onCloseClick && (
					<Button
						analyticsId={`${analyticsId}_dismiss_banner`}
						variant="link"
						aria-label="Dismiss banner"
						className={classNames('self-end active:bg-transparent text-inherit', {
							'pr-4': shouldRenderImageOnRight,
						})}
						onClick={onCloseClick}>
						<Cancel />
					</Button>
				)}

				{shouldRenderImageOnRight && (
					<div className="flex grow items-end justify-end">
						<BannerImage alt={title} imageAsset={imageAsset} imageUrl={imageUrl} imagePosition={imagePosition} />
					</div>
				)}
			</div>
		</div>
	);
};

const SMALL_BANNER_BG_COLOR: Record<SmallBannerVariant, string> = {
	primary: 'bg-bg-primary',
	secondary: 'bg-bg-tertiary',
	inverse: 'bg-bg-inverse',
	green: 'bg-accent-green-200',
	darkGreen: 'bg-accent-green-600',
	yellow: 'bg-util-caution-300',
	grey: 'bg-accent-grey-200',
};

const getCTAButtonTheme = (variant: SmallBannerVariant, ctaLayout: SmallBannerCtaLayout) => {
	if (ctaLayout === 'inlineLink') return 'link';
	switch (variant) {
		case 'green':
		case 'darkGreen':
		case 'yellow':
		case 'grey':
			return 'secondary';
		case 'primary':
		case 'secondary':
		case 'inverse':
		default:
			return 'primary';
	}
};
